import { Link } from 'react-router-dom';
import useScrollReveal from '../../hooks/useScrollReveal';
import { useGlobal } from '../../context/GlobalContext';
import {
  formatPriceText,
  getComingSoonLabel,
  getLowestPriceTextFromItems,
  getStartingAtLabel,
} from '../../lib/currency';
import styles from './ModelHeader.module.css';

const normalizeUnitQueryValue = (value = '') => String(value || '').trim().replace(/\s+/g, '');

const appendUnitParam = (url, modelTitle) => {
  const baseUrl = url || '/?contacto';
  const unitValue = normalizeUnitQueryValue(modelTitle);

  if (!unitValue) return baseUrl;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}unidad=${encodeURIComponent(unitValue)}`;
};

const ModelHeader = ({ data, items = [] }) => {
  const { companyInfo, locale, smartlinkWhatsapp } = useGlobal();
  const {
    titulo,
    subtitulo,
    precio,
    ctaLabel,
    ctaUrl,
    ctaUseWhatsapp,
    ctaWhatsappNumber,
    ctaWhatsappMessage,
  } = data;
  const mostrarPrecio = data?.mostrarPrecio !== false;

  const getFallbackWhatsappNumber = () => {
    const fromSocial = companyInfo?.socialMedia?.find((item) => item?.platform === 'whatsapp');
    if (fromSocial?.url) {
      const match = fromSocial.url.match(/(\d[\d\s\-+()]+)/);
      if (match) return match[1];
    }

    const fromContact = companyInfo?.contactInfo?.find((item) => item?.type === 'whatsapp');
    if (fromContact?.value) return fromContact.value;

    return null;
  };

  const buildWhatsappHref = (rawNumber, message, modelTitle) => {
    const number = (rawNumber || '').replace(/\D/g, '');
    if (!number) return null;

    const base = `https://wa.me/${number}`;
    const messageParts = [];

    if (message && message.trim()) {
      messageParts.push(message.trim());
    }

    if (modelTitle && modelTitle.trim()) {
      messageParts.push(modelTitle.trim());
    }

    if (messageParts.length > 0) {
      return `${base}?text=${encodeURIComponent(messageParts.join('\n\n'))}`;
    }

    return base;
  };

  const lowestPrice = mostrarPrecio ? getLowestPriceTextFromItems(items) : null;
  const hasStartingPrice = Boolean(precio || lowestPrice);

  let displayPrice;
  if (!mostrarPrecio) {
    displayPrice = null;
  } else if (precio) {
    displayPrice = formatPriceText(precio, companyInfo, locale);
  } else if (lowestPrice) {
    displayPrice = formatPriceText(lowestPrice, companyInfo, locale);
  } else {
    displayPrice = getComingSoonLabel(locale);
  }

  const whatsappHref = buildWhatsappHref(
    smartlinkWhatsapp || ctaWhatsappNumber || getFallbackWhatsappNumber(),
    ctaWhatsappMessage,
    titulo
  );
  const useWhatsappCta = Boolean(ctaUseWhatsapp && whatsappHref);
  const resolvedCtaUrl = useWhatsappCta ? whatsappHref : appendUnitParam(ctaUrl, titulo);
  const isInternalCta = resolvedCtaUrl.startsWith('/');

  const [ref, isVisible] = useScrollReveal({ threshold: 0.2 });

  return (
    <section 
      ref={ref}
      className={`${styles.modelHeader} ${isVisible ? styles['modelHeader--visible'] : ''}`}
    >
      <div className={styles.modelHeader__info}>
        <h1 className={styles.modelHeader__title}>{titulo}</h1>
        {subtitulo && (
          <span className={styles.modelHeader__subtitle}>({subtitulo})</span>
        )}
      </div>

      <div className={styles.modelHeader__actions}>
        {displayPrice && (
          <p className={styles.modelHeader__price}>
            {hasStartingPrice ? (
              <>
                {getStartingAtLabel(locale)} <span>{displayPrice}</span>
              </>
            ) : (
              <span>{displayPrice}</span>
            )}
          </p>
        )}
        {ctaLabel && isInternalCta && (
          <Link to={resolvedCtaUrl} className={styles.modelHeader__cta} title={ctaLabel}>
            {ctaLabel}
          </Link>
        )}
        {ctaLabel && !isInternalCta && (
          <a
            href={resolvedCtaUrl}
            className={styles.modelHeader__cta}
            target={useWhatsappCta ? '_blank' : undefined}
            rel={useWhatsappCta ? 'noopener noreferrer' : undefined}
            title={ctaLabel}
          >
            {ctaLabel}
          </a>
        )}
      </div>
    </section>
  );
};

export default ModelHeader;
